import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

export type Cr02DialogType = 'success' | 'error' | 'warning' | 'info';
export type Cr02DialogMode = 'alert' | 'confirm' | 'prompt' | 'prompt-dual';

export interface Cr02DialogConfig {
  mode?: Cr02DialogMode;
  type?: Cr02DialogType;
  title: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
  /** Confirm button color: brand (default), success (publish), or danger. */
  confirmTone?: 'brand' | 'success' | 'danger';
  inputLabel?: string;
  inputPlaceholder?: string;
  inputValue?: string;
  inputRequired?: boolean;
  /** Second field for prompt-dual (e.g. extracted value). */
  inputLabel2?: string;
  inputPlaceholder2?: string;
  inputValue2?: string;
  inputRequired2?: boolean;
}

export interface Cr02DualPromptResult {
  value: string;
  value2: string;
}

@Component({
  selector: 'app-cr02-dialog',
  templateUrl: './cr02-dialog.component.html',
  styleUrls: ['./cr02-dialog.component.scss']
})
export class Cr02DialogComponent {
  @Input() mode: Cr02DialogMode = 'alert';
  @Input() type: Cr02DialogType = 'info';
  @Input() title = '';
  @Input() message = '';
  @Input() confirmText = 'OK';
  @Input() cancelText = 'Cancel';
  @Input() danger = false;
  @Input() confirmTone: 'brand' | 'success' | 'danger' = 'brand';
  @Input() inputLabel = '';
  @Input() inputPlaceholder = '';
  @Input() inputValue = '';
  @Input() inputRequired = true;
  @Input() inputLabel2 = '';
  @Input() inputPlaceholder2 = '';
  @Input() inputValue2 = '';
  @Input() inputRequired2 = true;

  inputError = false;
  inputError2 = false;

  constructor(public activeModal: NgbActiveModal) {}

  applyConfig(config: Cr02DialogConfig): void {
    this.mode = config.mode || 'alert';
    this.type = config.type || (this.mode === 'confirm' ? 'warning' : 'info');
    this.title = config.title || '';
    this.message = config.message || '';
    this.confirmText = config.confirmText || (this.mode === 'confirm' ? 'Confirm' : 'OK');
    this.cancelText = config.cancelText || 'Cancel';
    this.danger = !!config.danger;
    this.confirmTone = config.confirmTone || (this.danger ? 'danger' : 'brand');
    this.inputLabel = config.inputLabel || '';
    this.inputPlaceholder = config.inputPlaceholder || '';
    this.inputValue = config.inputValue || '';
    this.inputRequired = config.inputRequired !== false;
    this.inputLabel2 = config.inputLabel2 || '';
    this.inputPlaceholder2 = config.inputPlaceholder2 || '';
    this.inputValue2 = config.inputValue2 || '';
    this.inputRequired2 = config.inputRequired2 !== false;
  }

  onConfirm(): void {
    if (this.mode === 'prompt') {
      const value = (this.inputValue || '').trim();
      if (this.inputRequired && !value) {
        this.inputError = true;
        return;
      }
      this.activeModal.close(value);
      return;
    }
    if (this.mode === 'prompt-dual') {
      const value = (this.inputValue || '').trim();
      const value2 = (this.inputValue2 || '').trim();
      this.inputError = this.inputRequired && !value;
      this.inputError2 = this.inputRequired2 && !value2;
      if (this.inputError || this.inputError2) {
        return;
      }
      this.activeModal.close({ value, value2 } as Cr02DualPromptResult);
      return;
    }
    this.activeModal.close(true);
  }

  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }
}
